/**
 * App-wide department UI helpers: hide internal units and always prefer Thai names over IDs.
 * Also: directory order = LOF id A–Z, then department name A–Z within each LOF.
 */
import { lofLabelForUi, normalizeLofId } from './lofs';
import { departmentIdScopeKey } from '../utils/normalizeIds';

/** Never show these departments in user-facing lists, charts, tables, filters, or exports. */
export const UI_HIDDEN_DEPARTMENT_IDS = new Set<string>(['NSO', 'CSSD']);

const UNGROUPED_LOF_KEY = '__none__';
/** Sort key so departments with missing LOF sort after all real LOFs. */
const MISSING_LOF_SORT_KEY = '\uffff';

export type DepartmentSortable = {
  id?: string | null;
  name?: string | null;
  lofId?: string | null;
};

export type LofGroupedDepartmentOption = { value: string; label: string };

export type LofGroupedDepartmentGroup = {
  groupKey: string;
  heading: string;
  options: LofGroupedDepartmentOption[];
};

/** Compare for directory order: LOF id A–Z, then department display name A–Z, then id. */
export function compareDepartmentsByLofThenName(a: DepartmentSortable, b: DepartmentSortable): number {
  const rawLofA = typeof a.lofId === 'string' ? a.lofId.trim() : '';
  const rawLofB = typeof b.lofId === 'string' ? b.lofId.trim() : '';
  const lofA = rawLofA ? normalizeLofId(rawLofA) : MISSING_LOF_SORT_KEY;
  const lofB = rawLofB ? normalizeLofId(rawLofB) : MISSING_LOF_SORT_KEY;
  const lofCmp = lofA.localeCompare(lofB, 'en', { sensitivity: 'base' });
  if (lofCmp !== 0) return lofCmp;

  const nameA = String(a.name ?? a.id ?? '').trim();
  const nameB = String(b.name ?? b.id ?? '').trim();
  const nameCmp = nameA.localeCompare(nameB, 'th', { sensitivity: 'base' });
  if (nameCmp !== 0) return nameCmp;

  return String(a.id ?? '').localeCompare(String(b.id ?? ''), 'en', { sensitivity: 'base' });
}

/** Flat list: group by LOF (via sort), LOFs A–Z, departments A–Z within each LOF. */
export function sortDepartmentsByLofThenName<T extends DepartmentSortable>(departments: readonly T[]): T[] {
  return [...departments].sort(compareDepartmentsByLofThenName);
}

/**
 * Sort department ids using a catalog for `lofId` / `name`.
 * Unknown ids sort after known ones (missing LOF key), by id.
 */
export function sortDepartmentIdsByLofThenName(
  departmentIds: readonly string[],
  catalog: ReadonlyArray<{ id: string; name?: string | null; lofId?: string | null }>,
): string[] {
  const byId = new Map<string, { id: string; name?: string | null; lofId?: string | null }>();
  for (const dept of catalog) {
    const id = String(dept.id ?? '').trim();
    if (!id) continue;
    byId.set(id, dept);
    byId.set(departmentIdScopeKey(id), dept);
  }

  return [...departmentIds].sort((left, right) => {
    const a = byId.get(left) ?? byId.get(departmentIdScopeKey(left));
    const b = byId.get(right) ?? byId.get(departmentIdScopeKey(right));
    return compareDepartmentsByLofThenName(
      { id: left, name: a?.name ?? left, lofId: a?.lofId ?? '' },
      { id: right, name: b?.name ?? right, lofId: b?.lofId ?? '' },
    );
  });
}

/**
 * Build MultiSelect-style groups: LOF sections A–Z by LOF id, departments A–Z by name within each.
 * Headings use Thai สายงาน names (never the acronym "LOF").
 */
export function buildLofGroupedDepartmentOptions(
  options: ReadonlyArray<{ id: string; name: string; lofId?: string | null }>,
  lofIdToName: Readonly<Record<string, string>> = {},
): { flatOptions: LofGroupedDepartmentOption[]; optionGroups: LofGroupedDepartmentGroup[] } {
  const byLof = new Map<string, Array<{ id: string; name: string; lofId?: string | null }>>();
  for (const option of options) {
    const raw = typeof option.lofId === 'string' ? option.lofId.trim() : '';
    const key = raw ? normalizeLofId(raw) : UNGROUPED_LOF_KEY;
    if (!byLof.has(key)) byLof.set(key, []);
    byLof.get(key)!.push(option);
  }

  const optionGroups: LofGroupedDepartmentGroup[] = Array.from(byLof.entries())
    .map(([lofKey, depts]) => {
      const sorted = sortDepartmentsByLofThenName(depts);
      const heading =
        lofKey === UNGROUPED_LOF_KEY
          ? 'ไม่ระบุสายงาน'
          : lofLabelForUi(lofKey, lofIdToName[lofKey]);
      return {
        groupKey: lofKey,
        heading,
        options: sorted.map((d) => ({ value: d.id, label: d.name })),
      };
    })
    .sort((a, b) => {
      if (a.groupKey === UNGROUPED_LOF_KEY) return 1;
      if (b.groupKey === UNGROUPED_LOF_KEY) return -1;
      return a.groupKey.localeCompare(b.groupKey, 'en', { sensitivity: 'base' });
    });

  const flatOptions = optionGroups.flatMap((g) => g.options);
  return { flatOptions, optionGroups };
}

const UNKNOWN_DEPARTMENT_LABEL = 'ไม่ทราบชื่อหน่วยงาน';

/** True when the string looks like a canonical department ID (ASCII id), not a Thai display name. */
export function looksLikeDepartmentId(value: string | null | undefined): boolean {
  const t = String(value ?? '').trim();
  if (!t) return false;
  return /^[A-Za-z0-9_]+$/.test(t);
}

export function isDepartmentHiddenInUi(departmentId: string | null | undefined): boolean {
  const key = departmentIdScopeKey(departmentId ?? '');
  if (!key) return false;
  for (const hidden of UI_HIDDEN_DEPARTMENT_IDS) {
    if (departmentIdScopeKey(hidden) === key) return true;
  }
  return false;
}

export function filterDepartmentsForUi<T extends { id: string }>(departments: readonly T[]): T[] {
  return departments.filter((dept) => !isDepartmentHiddenInUi(dept.id));
}

export function filterDepartmentIdsForUi(departmentIds: readonly string[]): string[] {
  return departmentIds.filter((id) => !isDepartmentHiddenInUi(id));
}

export function buildDepartmentIdToNameMap(
  departments: ReadonlyArray<{ id: string; name?: string | null }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const dept of departments) {
    const id = String(dept.id ?? '').trim();
    if (!id) continue;
    const name = typeof dept.name === 'string' ? dept.name.trim() : '';
    if (!name) continue;
    map.set(id, name);
    map.set(departmentIdScopeKey(id), name);
  }
  return map;
}

/**
 * Resolve a user-visible department label. Prefer `/departments` catalog Thai `name` over raw IDs.
 * Never return a bare ID when a catalog name exists (e.g. OPD_MED → คลินิกอายุรกรรม & Meta & DM).
 * Prefer `ไม่ทราบชื่อหน่วยงาน` over a bare ID when the catalog has no name.
 */
export function resolveDepartmentDisplayName(
  departmentId: string | null | undefined,
  departmentName: string | null | undefined,
  idToName?: ReadonlyMap<string, string> | null,
): string {
  const id = String(departmentId ?? '').trim();
  const rawName = String(departmentName ?? '').trim();

  const lookup = (key: string): string | undefined => {
    if (!idToName || !key) return undefined;
    return idToName.get(key) ?? idToName.get(departmentIdScopeKey(key));
  };

  const mappedFromId = lookup(id);
  const mappedFromName = lookup(rawName);

  if (mappedFromId && (!rawName || rawName === id || looksLikeDepartmentId(rawName))) {
    return mappedFromId;
  }
  if (mappedFromName && looksLikeDepartmentId(rawName)) {
    return mappedFromName;
  }
  if (rawName && !looksLikeDepartmentId(rawName)) return rawName;
  if (mappedFromId) return mappedFromId;
  return UNKNOWN_DEPARTMENT_LABEL;
}

type SubmissionDeptFields = {
  departmentId?: string | null;
  departmentName?: string | null;
  isCrossAudit?: boolean;
  crossAudit?: boolean;
  auditedDepartmentId?: string | null;
  auditedDepartmentName?: string | null;
};

/** Department id used for UI grouping (audited ward for cross-audit rows). */
export function submissionDisplayDepartmentId(submission: SubmissionDeptFields): string {
  const isCross = !!(submission.isCrossAudit || submission.crossAudit);
  if (isCross) {
    return String(submission.auditedDepartmentId ?? submission.auditedDepartmentName ?? '').trim();
  }
  return String(submission.departmentId ?? submission.departmentName ?? '').trim();
}

export function isSubmissionDepartmentHiddenInUi(submission: SubmissionDeptFields): boolean {
  return isDepartmentHiddenInUi(submissionDisplayDepartmentId(submission));
}

export function filterSubmissionsForUi<T extends SubmissionDeptFields>(submissions: readonly T[]): T[] {
  return submissions.filter((row) => !isSubmissionDepartmentHiddenInUi(row));
}

export function resolveSubmissionDepartmentDisplayName(
  submission: SubmissionDeptFields,
  idToName?: ReadonlyMap<string, string> | null,
): string {
  const isCross = !!(submission.isCrossAudit || submission.crossAudit);
  if (isCross) {
    const auditedId = String(submission.auditedDepartmentId ?? '').trim();
    const auditedName = String(submission.auditedDepartmentName ?? '').trim();
    if (auditedId || auditedName) {
      return resolveDepartmentDisplayName(auditedId, auditedName, idToName);
    }
  }
  return resolveDepartmentDisplayName(submission.departmentId, submission.departmentName, idToName);
}
